import { forwardRef, useEffect, useRef, useState } from 'react'
import type { ContentProps } from '~/components/option-menu/components/item'
import { OptionMenu } from '~/index'
import { useSelect } from '../../hook/use-select'
import { Select, useSelectContext } from '../../select'
import type { SelectFieldProps, SelectScopedProps } from '../../select.types'
import { selectFieldVariants } from './index.styles'

export const SelectField = forwardRef<HTMLDivElement, SelectFieldProps>(
  ({
    children,
    __scopeInputSelect,
    onChangeSelectionValue,
    value,
    optionMaxHeight,
    ...props
  }: SelectScopedProps<SelectFieldProps>) => {
    const {
      size,
      hasError,
      disabled,
      readOnly,
      placeholder,
      options = [],
    } = useSelectContext('InputSelectField', __scopeInputSelect)
    const [isOpen, setIsOpen] = useState(false)
    const { setSelectedOption, selectedOption } = useSelect()
    const wrapperRef = useRef<HTMLDivElement>(null)

    const initOption: ContentProps = {
      label: placeholder ?? 'Selecione...',
      value: '',
    }

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
      if (value) {
        const selected = options.find(option => option.value === value)
        return setSelectedOption(selected)
      }

      return setSelectedOption(initOption)
    }, [value])

    useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        if (
          wrapperRef.current &&
          !wrapperRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false)
        }
      }
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [])

    const useSelectedOption = (option: ContentProps) => {
      setSelectedOption(option)
      setIsOpen(false)
      onChangeSelectionValue?.(option.value)
    }

    const useOpenMenu = () => {
      if (!disabled && !readOnly) {
        setIsOpen(!isOpen)
      }
    }

    const dataAttributes = () => {
      const data = Object.keys(props).reduce(
        (acc, key) => {
          if (key.startsWith('data-')) {
            acc[key] = (props as Record<string, string>)[key]
          }
          return acc
        },
        {} as Record<string, string>
      )
      return data
    }

    return (
      <div ref={wrapperRef} className="relative flex w-full flex-col">
        <div
          {...dataAttributes()}
          className={selectFieldVariants({
            size,
            hasError,
            readOnly,
            disabled,
            ...props,
          })}
          onClick={() => useOpenMenu()}
          onKeyUp={() => useOpenMenu()}
          data-testid="select-field"
        >
          {selectedOption ? selectedOption.label : initOption.label}

          <Select.Icon
            symbol={isOpen ? 'rdicon-chevron-up' : 'rdicon-chevron-down'}
            color={
              disabled
                ? 'colorTextNeutralDisabled'
                : readOnly
                  ? 'colorTextNeutralReadonly'
                  : 'colorTextNeutralDefault'
            }
          />
        </div>
        {isOpen && (
          <div
            className="x-full absolute top-full left-0 z-10 w-full py-fivepulse"
            data-testid="select-option-menu"
          >
            <OptionMenu.Root type="single" {...props}>
              <OptionMenu.List maxHeight={optionMaxHeight}>
                {options?.map((content, index) => (
                  <OptionMenu.Item
                    key={`${content.label}-${index}`}
                    content={content}
                    onPress={() => useSelectedOption(content)}
                    disabled={disabled}
                    data-testid="select-item"
                  />
                ))}
              </OptionMenu.List>
            </OptionMenu.Root>
          </div>
        )}
      </div>
    )
  }
)

SelectField.displayName = 'InputSelectField'
